import axios from "axios";
import { apiFetch, getToken, handleUnauthorized } from "./http";

const api = axios.create({ baseURL: "/api" });

// Aggiunge automaticamente il token se presente
api.interceptors.request.use((config) => {
    const token = getToken();
    if (token) {
        config.headers = config.headers ?? {};
        (config.headers as any).Authorization = `Bearer ${token}`;
    }
    return config;
});

// Gestisce 401 globalmente: pulisce token e reindirizza al login
api.interceptors.response.use(
    (res) => res,
    (error) => {
        if (error?.response?.status === 401) {
            handleUnauthorized();
        }
        return Promise.reject(error);
    }
);

const fetchWithTimeout = async (url: string, timeoutMs = 2000) => {
    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeoutMs);
    try {
        const res = await apiFetch(url, { signal: controller.signal });
        return res;
    } finally {
        clearTimeout(id);
    }
};

export const getSystemStatus = async () => {
    const check = async (url: string, timeoutMs = 2000) => {
        try {
            const res = await fetchWithTimeout(url, timeoutMs);
            if (!res.ok) return "ko";
            const data = await res.json().catch(() => ({} as any));
            if (typeof data?.status === "string") return data.status;
            return "ok";
        } catch {
            return "ko";
        }
    };

    const [backend, db, ahr] = await Promise.all([
        check("/api/health", 2000),
        check("/api/system/status/db", 2000),
        check("/api/system/status/ahr", 5000),
    ]);

    return { backend, db, ahr };
};

export const checkBackendStatus = async (): Promise<string> => {
    try {
        const res = await fetchWithTimeout("/api/health", 2000);
        return res.ok ? "ok" : "ko";
    } catch {
        return "ko";
    }
};

export const checkDbStatus = async (): Promise<string> => {
    try {
        const res = await fetchWithTimeout("/api/system/status/db", 2000);
        if (!res.ok) return "ko";
        const data = await res.json().catch(() => ({} as any));
        return typeof data?.status === "string" ? data.status : "ok";
    } catch {
        return "ko";
    }
};

export const checkAhrStatus = async (): Promise<string> => {
    try {
        const res = await fetchWithTimeout("/api/system/status/ahr", 5000);
        if (!res.ok) return "ko";
        const data = await res.json().catch(() => ({} as any));
        return typeof data?.status === "string" ? data.status : "ok";
    } catch {
        return "ko";
    }
};

export type AuthMe = {
    id: number;
    username?: string | null;
    email: string;
    full_name?: string | null;
    disabled?: boolean;
    roles: string[];
};

export const loginWithEmail = async (email: string, password: string): Promise<string> => {
    const body = new URLSearchParams();
    body.set("username", email.trim());
    body.set("password", password);
    const res = await fetch("/api/auth/token", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
    });
    if (!res.ok) {
        const err = await res.json().catch(() => ({} as any));
        throw new Error(err?.detail || "Credenziali non valide");
    }
    const data = await res.json();
    const token = data?.access_token;
    if (!token) throw new Error("Token non ricevuto");
    localStorage.setItem("auth_token", token);
    return token;
};

export const getMe = async (): Promise<AuthMe> => {
    const { data } = await api.get("/auth/me");
    return data;
};

export const createUserByAdmin = async (email: string, password: string, role: "admin" | "user") => {
    const { data } = await api.post("/auth/invitations", { email, password, role });
    return data;
};

export const updateProfile = async (payload: { full_name?: string; email?: string }) => {
    const { data } = await api.patch("/auth/me", payload);
    return data as AuthMe;
};

export const changePassword = async (current_password: string, new_password: string) => {
    const { data } = await api.post("/auth/me/change-password", { current_password, new_password });
    return data;
};

export default api;